import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Gift, 
  Sparkles, 
  Mail, 
  ArrowRight, 
  Lock, 
  Loader2 
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ComingSoonProps {
  onAccessGranted: () => void;
}

const ACCESS_CODE = "shakatah2025";

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function ComingSoon({ onAccessGranted }: ComingSoonProps) {
  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [showAccessInput, setShowAccessInput] = useState(false);
  const [error, setError] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (accessCode.toLowerCase() === ACCESS_CODE.toLowerCase()) {
      localStorage.setItem("shakatah_access", "granted");
      onAccessGranted();
    } else {
      setError("Invalid access code");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    const trimmedEmail = email.trim().toLowerCase();
    
    try {
      const { error } = await supabase
        .from('waitlist_signups')
        .insert({ email: trimmedEmail });
      
      if (error) {
        if (error.code === '23505') {
          toast.info("You're already on the waitlist!");
          setSubscribed(true);
          setEmail("");
          return;
        } else {
          throw error;
        }
      }
      
      // Send confirmation email
      try {
        await supabase.functions.invoke('send-waitlist-confirmation', {
          body: { email: trimmedEmail }
        });
      } catch (emailErr) {
        console.error("Failed to send confirmation email:", emailErr);
      }
      
      toast.success("Thanks! Check your inbox for a confirmation.");
      setSubscribed(true);
      setEmail("");
    } catch (err) {
      console.error("Waitlist signup error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30 flex flex-col">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-primary-foreground mb-6 shadow-lg shadow-primary/25">
            <Gift className="w-8 h-8" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-3 tracking-tight">
            Shakatah
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-6">
            Turn celebrations into sales
          </p>
          
          {/* Stealth Mode Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-muted-foreground text-sm font-medium">
            <Lock className="w-4 h-4" />
            Stealth Mode
          </div>

        </div>

        {/* Email Signup Section */}
        <div className="w-full max-w-md text-center">
          {!subscribed ? (
            <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12"
                  required
                />
              </div>
              <Button type="submit" size="lg" className="h-12" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Notify Me
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
              <p className="text-primary font-medium">
                🎉 You're on the list! We'll notify you when we launch.
              </p>
            </div>
          )}
        </div>

        {/* Tester Access */}
        <div className="mt-12 text-center">
          {!showAccessInput ? (
            <button
              onClick={() => setShowAccessInput(true)}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Lock className="w-4 h-4" />
              Have an access code?
            </button>
          ) : (
            <form onSubmit={handleAccessSubmit} className="max-w-xs mx-auto">
              <p className="text-sm text-muted-foreground mb-3">
                Enter your tester access code
              </p>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder="Access code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  className="h-10"
                  autoFocus
                />
                <Button type="submit" size="sm" className="h-10">
                  Enter
                </Button>
              </div>
              {error && (
                <p className="text-destructive text-sm mt-2">{error}</p>
              )}
            </form>
          )}
        </div>
      </div>

    </div>
  );
}
